import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  CalendarClock, Plus, Loader2, Trash2, Code2, Inbox, Video, Clock,
  CheckCircle2, Link2, Copy, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIFECYCLE = ['subscriber', 'lead', 'mql', 'sql', 'opportunity', 'customer'];
const DAYS = [{ v: 1, l: 'Mon' }, { v: 2, l: 'Tue' }, { v: 3, l: 'Wed' }, { v: 4, l: 'Thu' }, { v: 5, l: 'Fri' }, { v: 6, l: 'Sat' }, { v: 7, l: 'Sun' }];
const TIMEZONES = ['Europe/London', 'Europe/Dublin', 'America/New_York', 'America/Los_Angeles', 'UTC'];

const emptyPage = () => ({
  name: '', description: '', duration_min: 30, buffer_min: 0,
  availability: { days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00', timezone: 'Europe/London' },
  online_meeting: true, location: '', collect_phone: true, collect_notes: true,
  lifecycle_stage: 'lead', list_id: 'none', notify_email: '',
  success_message: "Your meeting is booked - check your inbox for the calendar invite.",
});

const Meetings = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outlook, setOutlook] = useState(null);
  const [builder, setBuilder] = useState(null);
  const [bookingsFor, setBookingsFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [params, setParams] = useSearchParams();

  const fetchStatus = useCallback(async () => {
    try { const r = await axios.get(`${API}/integrations/microsoft/status`); setOutlook(r.data); } catch { /* ignore */ }
  }, []);
  const fetchPages = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/booking-pages`); setPages(r.data.booking_pages || []); }
    catch { toast.error('Failed to load booking pages'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); fetchPages(); }, [fetchStatus, fetchPages]);

  useEffect(() => {
    const o = params.get('outlook');
    if (o === 'connected') { toast.success('Outlook calendar connected'); fetchStatus(); params.delete('outlook'); setParams(params, { replace: true }); }
    else if (o === 'error') { toast.error('Could not connect Outlook. Please try again.'); params.delete('outlook'); setParams(params, { replace: true }); }
  }, [params, setParams, fetchStatus]);

  const connectOutlook = async () => {
    try {
      const r = await axios.post(`${API}/integrations/microsoft/connect-url`, { base_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Outlook is not configured yet.');
    }
  };
  const disconnectOutlook = async () => {
    if (!window.confirm('Disconnect Outlook calendar?')) return;
    try { await axios.post(`${API}/integrations/microsoft/disconnect`); toast.success('Outlook disconnected'); fetchStatus(); }
    catch { toast.error('Failed to disconnect'); }
  };

  const handleDelete = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete booking page "${p.name}"?`)) return;
    try { await axios.delete(`${API}/booking-pages/${p.id}`); toast.success('Deleted'); fetchPages(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="meetings-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2 flex items-center gap-3">
            <CalendarClock className="w-8 h-8 text-violet-400" />
            Meetings
          </h1>
          <p className="text-slate-400">Let prospects book time that syncs with your Outlook calendar.</p>
        </div>
        <Button className="bg-violet-600 hover:bg-violet-500" onClick={() => setBuilder(emptyPage())} data-testid="new-booking-page-btn">
          <Plus className="w-4 h-4 mr-2" /> New Booking Page
        </Button>
      </div>

      {/* Outlook connection */}
      <Card className={`border ${outlook?.connected ? 'bg-emerald-950/20 border-emerald-800/40' : 'bg-slate-900/50 border-slate-800'}`} data-testid="outlook-card">
        <CardContent className="flex items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${outlook?.connected ? 'bg-emerald-600/20' : 'bg-slate-800'}`}>
              {outlook?.connected ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Video className="w-5 h-5 text-slate-400" />}
            </div>
            <div>
              <p className="text-sm font-medium text-white">
                {outlook?.connected ? `Outlook connected - ${outlook.email}` : 'Connect your Outlook calendar'}
              </p>
              <p className="text-xs text-slate-400">
                {outlook?.connected ? 'Bookings sync to your calendar with a Teams link, and busy times are blocked.'
                  : outlook?.configured === false ? 'Awaiting Azure app credentials. Bookings still work and create contacts.'
                  : 'Sync bookings to Outlook and auto-block busy times.'}
              </p>
            </div>
          </div>
          {outlook?.connected ? (
            <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={disconnectOutlook} data-testid="disconnect-outlook-btn">Disconnect</Button>
          ) : (
            <Button className="bg-violet-600 hover:bg-violet-500" onClick={connectOutlook} disabled={outlook?.configured === false} data-testid="connect-outlook-btn">
              <Link2 className="w-4 h-4 mr-2" /> Connect Outlook
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="meetings-loading"><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></div>
      ) : pages.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800"><CardContent className="text-center py-16 text-slate-500">No booking pages yet. Create one to start taking meetings.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {pages.map(p => (
            <div key={p.id} className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 space-y-3" data-testid={`booking-page-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-white font-medium">{p.name}</p>
                <button onClick={(e) => handleDelete(p, e)} className="text-slate-600 hover:text-rose-400" data-testid={`delete-booking-page-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.duration_min} min</span>
                {p.online_meeting && <span className="flex items-center gap-1"><Video className="w-3 h-3" /> Teams</span>}
                <span className="flex items-center gap-1"><Inbox className="w-3 h-3" /> {p.booking_count || 0}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-7 text-xs" onClick={() => setBuilder({ ...p, list_id: p.list_id || 'none', lifecycle_stage: p.lifecycle_stage || 'none' })} data-testid={`edit-booking-page-${p.id}`}>Edit</Button>
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-7 text-xs" onClick={() => setShareFor(p)} data-testid={`share-booking-page-${p.id}`}><Code2 className="w-3 h-3 mr-1" /> Embed</Button>
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-7 text-xs" onClick={() => setBookingsFor(p)} data-testid={`bookings-booking-page-${p.id}`}><Inbox className="w-3 h-3 mr-1" /> Bookings</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {builder && <PageBuilder initial={builder} onClose={() => setBuilder(null)} onSaved={() => { setBuilder(null); fetchPages(); }} />}
      {bookingsFor && <BookingsDialog page={bookingsFor} onClose={() => setBookingsFor(null)} />}
      {shareFor && <ShareDialog page={shareFor} onClose={() => setShareFor(null)} />}
    </div>
  );
};

const PageBuilder = ({ initial, onClose, onSaved }) => {
  const [p, setP] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState([]);

  useEffect(() => {
    axios.get(`${API}/lists`, { params: { object_type: 'contact' } }).then(r => setLists((r.data.lists || []).filter(l => l.list_type === 'static'))).catch(() => {});
  }, []);

  const av = p.availability;
  const setAv = (patch) => setP({ ...p, availability: { ...av, ...patch } });
  const toggleDay = (d) => setAv({ days: av.days.includes(d) ? av.days.filter(x => x !== d) : [...av.days, d].sort() });

  // Multiple time windows per day (e.g. 12:00-14:00 and 16:00-17:00)
  const windows = (av.windows && av.windows.length) ? av.windows : [{ start: av.start || '09:00', end: av.end || '17:00' }];
  const setWindows = (w) => setAv({ windows: w, start: w[0]?.start || '09:00', end: w[w.length - 1]?.end || '17:00' });
  const updateWindow = (i, patch) => setWindows(windows.map((w, idx) => idx === i ? { ...w, ...patch } : w));
  const addWindow = () => setWindows([...windows, { start: '14:00', end: '17:00' }]);
  const removeWindow = (i) => setWindows(windows.length > 1 ? windows.filter((_, idx) => idx !== i) : windows);

  const save = async () => {
    if (!p.name.trim()) { toast.error('Name required'); return; }
    if (av.days.length === 0) { toast.error('Pick at least one available day'); return; }
    setSaving(true);
    try {
      const payload = {
        name: p.name.trim(), description: p.description || null,
        duration_min: Number(p.duration_min), buffer_min: Number(p.buffer_min || 0),
        availability: { days: av.days, windows, start: windows[0].start, end: windows[windows.length - 1].end, timezone: av.timezone },
        online_meeting: p.online_meeting, location: p.online_meeting ? null : (p.location || null),
        collect_phone: p.collect_phone, collect_notes: p.collect_notes,
        lifecycle_stage: p.lifecycle_stage !== 'none' ? p.lifecycle_stage : null,
        list_id: p.list_id !== 'none' ? p.list_id : null,
        notify_email: p.notify_email || null, success_message: p.success_message,
      };
      if (p.id) await axios.patch(`${API}/booking-pages/${p.id}`, payload);
      else await axios.post(`${API}/booking-pages`, payload);
      toast.success(p.id ? 'Booking page updated' : 'Booking page created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="page-builder">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">{p.id ? 'Edit Booking Page' : 'New Booking Page'}</DialogTitle>
          <DialogDescription className="text-slate-400">Set your availability and what happens when someone books.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label className="text-slate-300">Name *</Label>
            <Input value={p.name} onChange={e => setP({ ...p, name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="e.g. Care intro call" data-testid="page-name-input" />
          </div>
          <div><Label className="text-slate-300">Description</Label>
            <Input value={p.description} onChange={e => setP({ ...p, description: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="What's this meeting about?" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-slate-400 text-xs">Duration (min)</Label>
              <Select value={String(p.duration_min)} onValueChange={v => setP({ ...p, duration_min: Number(v) })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-9" data-testid="page-duration"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">{[15, 30, 45, 60].map(m => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-400 text-xs">Buffer between (min)</Label>
              <Select value={String(p.buffer_min)} onValueChange={v => setP({ ...p, buffer_min: Number(v) })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">{[0, 5, 10, 15, 30].map(m => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Availability */}
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Availability</p>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map(d => (
                <button key={d.v} onClick={() => toggleDay(d.v)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${av.days.includes(d.v) ? 'bg-violet-600 border-violet-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400'}`} data-testid={`day-${d.v}`}>{d.l}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div><Label className="text-slate-400 text-xs">Timezone</Label>
                <Select value={av.timezone} onValueChange={v => setAv({ timezone: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Time windows <span className="text-slate-600">(each applies to every selected day)</span></Label>
                {windows.map((w, i) => (
                  <div key={i} className="flex items-end gap-2" data-testid={`time-window-${i}`}>
                    <div className="flex-1">
                      <span className="text-[10px] text-slate-500">From</span>
                      <Input type="time" value={w.start} onChange={e => updateWindow(i, { start: e.target.value })} className="bg-slate-900 border-slate-800 h-9" data-testid={`window-${i}-start`} />
                    </div>
                    <div className="flex-1">
                      <span className="text-[10px] text-slate-500">To</span>
                      <Input type="time" value={w.end} onChange={e => updateWindow(i, { end: e.target.value })} className="bg-slate-900 border-slate-800 h-9" data-testid={`window-${i}-end`} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeWindow(i)} disabled={windows.length === 1} className="text-slate-400 hover:text-red-400 h-9 w-9 shrink-0" data-testid={`remove-window-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addWindow} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="add-time-window-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add time window
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg p-3">
            <div><Label className="text-slate-300 text-sm">Add Microsoft Teams link</Label><p className="text-[11px] text-slate-500">Auto-creates an online meeting on each booking.</p></div>
            <Switch checked={p.online_meeting} onCheckedChange={v => setP({ ...p, online_meeting: v })} data-testid="page-online-toggle" />
          </div>
          {!p.online_meeting && (
            <div><Label className="text-slate-400 text-xs">Location</Label>
              <Input value={p.location} onChange={e => setP({ ...p, location: e.target.value })} className="bg-slate-950 border-slate-800 h-9" placeholder="e.g. Phone call, or an address" />
            </div>
          )}

          {/* On booking */}
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">When someone books</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-slate-400 text-xs">Lifecycle stage</Label>
                <Select value={p.lifecycle_stage} onValueChange={v => setP({ ...p, lifecycle_stage: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-9" data-testid="page-lifecycle"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800"><SelectItem value="none">- none -</SelectItem>{LIFECYCLE.map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-slate-400 text-xs">Add to list</Label>
                <Select value={p.list_id} onValueChange={v => setP({ ...p, list_id: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-9" data-testid="page-list"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800"><SelectItem value="none">- none -</SelectItem>{lists.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-slate-400 text-xs">Notify email</Label>
              <Input value={p.notify_email} onChange={e => setP({ ...p, notify_email: e.target.value })} className="bg-slate-900 border-slate-800 h-9" placeholder="you@company.com" data-testid="page-notify-email" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={p.collect_phone} onCheckedChange={v => setP({ ...p, collect_phone: v })} /> Ask for phone</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={p.collect_notes} onCheckedChange={v => setP({ ...p, collect_notes: v })} /> Ask for notes</label>
            </div>
          </div>

          <div><Label className="text-slate-400 text-xs">Success message</Label>
            <Input value={p.success_message} onChange={e => setP({ ...p, success_message: e.target.value })} className="bg-slate-950 border-slate-800 h-9" />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-500" onClick={save} disabled={saving} data-testid="save-booking-page-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : (p.id ? 'Save changes' : 'Create page')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BookingsDialog = ({ page, onClose }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/booking-pages/${page.id}/bookings`).then(r => setBookings(r.data.bookings || [])).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [page.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bookings-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">{page.name} - Bookings</DialogTitle>
          <DialogDescription className="text-slate-400">{page.booking_count || 0} total</DialogDescription>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          : bookings.length === 0 ? <p className="text-sm text-slate-600 italic py-8 text-center">No bookings yet.</p>
          : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {bookings.map(b => (
                <div key={b.id} className="bg-slate-950 border border-slate-800 rounded-md p-3" data-testid={`booking-${b.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white">{b.full_name} <span className="text-slate-500 text-xs">{b.email}</span></span>
                    {b.synced_to_outlook ? <Badge variant="outline" className="text-[10px] border-emerald-700/50 text-emerald-400">Synced</Badge> : <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500">Not synced</Badge>}
                  </div>
                  <p className="text-xs text-slate-400">{new Date(b.start).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} ({b.timezone})</p>
                  {b.notes && <p className="text-xs text-slate-500 mt-1">{b.notes}</p>}
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
};

const ShareDialog = ({ page, onClose }) => {
  const hostedUrl = `${window.location.origin}/m/${page.id}`;
  const iframe = `<iframe src="${hostedUrl}" width="100%" height="720" frameborder="0" style="border:0;max-width:760px;"></iframe>`;
  const copy = (t, l) => { navigator.clipboard.writeText(t); toast.success(`${l} copied`); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="share-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2"><Code2 className="w-5 h-5 text-violet-400" /> Embed "{page.name}"</DialogTitle>
          <DialogDescription className="text-slate-400">Share the booking link or embed it on your site.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Booking link</Label>
            <div className="flex gap-2">
              <Input readOnly value={hostedUrl} className="bg-slate-950 border-slate-800 text-slate-300 text-xs" data-testid="booking-hosted-url" />
              <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700" onClick={() => copy(hostedUrl, 'Link')} data-testid="copy-booking-link-btn"><Copy className="w-4 h-4" /></Button>
              <a href={hostedUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="bg-slate-900 border-slate-700"><ExternalLink className="w-4 h-4" /></Button></a>
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Embed code (iframe)</Label>
            <textarea readOnly value={iframe} rows={3} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-xs text-slate-300 font-mono" data-testid="booking-embed-code" />
            <Button size="sm" className="bg-violet-600 hover:bg-violet-500 mt-2" onClick={() => copy(iframe, 'Embed code')} data-testid="copy-booking-embed-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy embed code</Button>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Meetings;
